var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import classNames from "classnames";
import { SkeletonLoader } from "@widget/loaders";
import { getFontAwesomeIcon } from "@utils";
import { UXPContext } from "@core/context/Context";
import { Dropdown } from "@widget/popups/Dropdown";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { isObjectInfoCardProps, ObjectInfoCard } from "./ObjectInfoCardComponent";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import ActionsListComponent from "../Common/ActionListComponent";
import { ObjectDetailsPanelHeader, isObjectDetailsPanelHeaderProps } from "@widget/list/Tables/object-search/ObjectDetailsPanelHeader";
import { useRouterContext, useSearchParams } from "@hooks/useRouteHandlers";
import { buildInjectedAdditionalDetails } from "@core/pages/injected-tabs/buildInjectedAdditionalDetails";
import { registerMountedObjectType, unregisterMountedObjectType } from "@core/pages/injected-tabs/mountedInjectedTabsRegistry";
import { onInjectionRegistryChange } from "@core/pages/injected-tabs/tabInjectionRegistry";
import { registerHardcodedTabs, unregisterHardcodedTabs } from "@core/pages/injected-tabs/hardcodedTabsRegistry";
import { useIsPageEditorPreview } from "@core/context/PageEditorPreviewContext";
import { useMultiEventSubscriber } from "@realtime/eventbus/useEventBus";
const ObjectDetailsPanelBase = forwardRef((props, ref) => {
    const context = useContext(UXPContext);
    const isInPreview = useIsPageEditorPreview();
    const { onClose, showCloseButton = true, title, tabs, toolbarItems, generalDetails, otherDetails, additionlDetails, data, appendToURL = true, urlParams: propUrlParams, objectType, objectKey } = props;
    const { navigate, generateURL } = useRouterContext();
    // Merge URL params with defaults
    const urlParams = useMemo(() => ({
        detailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.detailsTab) || 'dt',
        additionalDetailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.additionalDetailsTab) || 'ad'
    }), [propUrlParams]);
    const searchParams = useSearchParams();
    const dt = searchParams[urlParams.detailsTab];
    const ad = searchParams[urlParams.additionalDetailsTab];
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAdditionlDetailsTab, setSelectedAdditionalDetailsTab] = useState(null);
    const [selectedTabId, setSelectedTabId] = useState(null);
    const [additionalDetailsRefreshKeys, setAdditionalDetailsRefreshKeys] = useState({});
    const [tabOverflowFrom, setTabOverflowFrom] = useState(null);
    const [tabOverflowOpen, setTabOverflowOpen] = useState(false);
    const tabsContainerRef = useRef(null);
    const tabItemRefs = useRef(new Map());
    const tabWidthCache = useRef(new Map());
    const tabOverflowDropdownRef = useRef(null);
    const [toolbarLeftOverflowFrom, setToolbarLeftOverflowFrom] = useState(null);
    const [toolbarRightOverflowFrom, setToolbarRightOverflowFrom] = useState(null);
    const [toolbarLeftOverflowOpen, setToolbarLeftOverflowOpen] = useState(false);
    const [toolbarRightOverflowOpen, setToolbarRightOverflowOpen] = useState(false);
    const toolbarLeftContainerRef = useRef(null);
    const toolbarRightContainerRef = useRef(null);
    const toolbarLeftItemRefs = useRef(new Map());
    const toolbarRightItemRefs = useRef(new Map());
    const toolbarLeftWidthCache = useRef(new Map());
    const toolbarRightWidthCache = useRef(new Map());
    const toolbarLeftOverflowDropdownRef = useRef(null);
    const toolbarRightOverflowDropdownRef = useRef(null);
    // Normalize old API to new tab structure
    const effectiveTabs = useMemo(() => {
        if (tabs)
            return tabs;
        // Old API: create single implicit tab
        return [{
                id: 'general',
                toolbarItems,
                details: generalDetails
            }];
    }, [tabs, toolbarItems, generalDetails]);
    const shouldShowTabs = effectiveTabs.length > 1;
    const activeTab = useMemo(() => {
        // Priority: URL parameter (if appendToURL) > selectedTabId > first tab
        const tabId = (appendToURL && dt) ? dt : selectedTabId;
        if (!tabId && effectiveTabs.length > 0) {
            return effectiveTabs[0];
        }
        // Validate tab ID exists
        const foundTab = effectiveTabs.find(t => t.id === tabId);
        return foundTab || effectiveTabs[0];
    }, [selectedTabId, effectiveTabs, appendToURL, dt]);
    // Resolve toolbar items (static or function-based)
    const resolvedToolbarItems = useMemo(() => {
        if (!(activeTab === null || activeTab === void 0 ? void 0 : activeTab.toolbarItems))
            return null;
        if (typeof activeTab.toolbarItems === 'function') {
            return row ? activeTab.toolbarItems(row) : null;
        }
        return activeTab.toolbarItems;
    }, [activeTab, row]);
    // Build injected additional details from registered tab contributions
    const [registryVersion, setRegistryVersion] = useState(0);
    useEffect(() => {
        if (!objectType)
            return;
        return onInjectionRegistryChange(() => setRegistryVersion(v => v + 1));
    }, [objectType]);
    const injectedDetails = useMemo(() => {
        var _a;
        if (!objectType)
            return [];
        return buildInjectedAdditionalDetails({
            objectTabs: (context === null || context === void 0 ? void 0 : context.objectTabs) || {},
            objectType,
            objectKey,
            parentProps: props,
            userAppRoles: (context === null || context === void 0 ? void 0 : context.appRoles) || {},
            userGroupKey: ((_a = context === null || context === void 0 ? void 0 : context.userDetails) === null || _a === void 0 ? void 0 : _a.UserGroupKey) || '',
        });
    }, [objectType, objectKey, context === null || context === void 0 ? void 0 : context.objectTabs, context === null || context === void 0 ? void 0 : context.appRoles, context === null || context === void 0 ? void 0 : context.userDetails, registryVersion]);
    // Merge host-declared additional details with injected ones
    const allAdditionalDetails = useMemo(() => {
        const host = additionlDetails || [];
        return [...host, ...injectedDetails];
    }, [additionlDetails, injectedDetails]);
    // Build event → tab id mapping for auto-refresh subscriptions
    const eventTabMap = useMemo(() => {
        const map = {};
        allAdditionalDetails.forEach(tab => {
            var _a;
            (_a = tab.events) === null || _a === void 0 ? void 0 : _a.forEach(ev => {
                var _a;
                ((_a = map[ev]) !== null && _a !== void 0 ? _a : (map[ev] = [])).push(tab.id);
            });
        });
        return map;
    }, [allAdditionalDetails]);
    const allTabEvents = useMemo(() => Object.keys(eventTabMap), [eventTabMap]);
    useMultiEventSubscriber('uxp-obsdp', {
        events: allTabEvents,
        sharedCallback: (_payload, eventName) => {
            var _a;
            const tabIds = (_a = eventTabMap[eventName]) !== null && _a !== void 0 ? _a : [];
            if (tabIds.length === 0)
                return;
            setAdditionalDetailsRefreshKeys(prev => {
                const next = Object.assign({}, prev);
                tabIds.forEach(id => { next[id] = (next[id] || 0) + 1; });
                return next;
            });
        }
    });
    // Register / unregister this objectType in the mounted registry so ConfiguredPage
    // can detect that this page has injectable tab slots
    useEffect(() => {
        if (!objectType || isInPreview)
            return;
        registerMountedObjectType(objectType);
        return () => unregisterMountedObjectType(objectType);
    }, [objectType, isInPreview]);
    // Register hardcoded tabs so the page editor preview can include them in the gutter
    useEffect(() => {
        if (!objectType || !(additionlDetails === null || additionlDetails === void 0 ? void 0 : additionlDetails.length) || isInPreview)
            return;
        registerHardcodedTabs(objectType, additionlDetails.map(t => ({
            id: t.id,
            icon: t.icon,
            label: t.label,
        })));
        return () => unregisterHardcodedTabs(objectType);
    }, [objectType, additionlDetails, isInPreview]);
    const hasAdditionalDetails = allAdditionalDetails.length > 0;
    const hasOtherDetails = (!!otherDetails);
    const hasGeneralDetails = (!!(activeTab === null || activeTab === void 0 ? void 0 : activeTab.details));
    const hasToolbarItems = (((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) && resolvedToolbarItems.left.length > 0) || ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) && resolvedToolbarItems.right.length > 0));
    const loadData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            let rowData;
            if (typeof data === 'function') {
                rowData = yield data();
            }
            else {
                rowData = data;
            }
            setRow(rowData);
        }
        catch (error) {
            console.error("Failed to load row data:", error);
            setRow(null); // Handle error state as needed
        }
        finally {
            setLoading(false);
        }
    }), [data]);
    useImperativeHandle(ref, () => ({
        refresh: () => {
            loadData();
        }
    }));
    useEffect(() => {
        loadData();
    }, [loadData]);
    const renderDetails = useCallback((details) => {
        if (!row)
            return null;
        if (typeof details === 'function')
            return details(row, loading);
        return details;
    }, [row, loading]);
    const renderGeneralDetails = useCallback(() => {
        if (!row && !loading)
            return _jsx("div", { children: "Data not available" });
        if (!(activeTab === null || activeTab === void 0 ? void 0 : activeTab.details))
            return null;
        const details = activeTab.details;
        if (typeof details === 'function') {
            const res = details(row);
            if (isObjectInfoCardProps(res))
                return _jsx(ObjectInfoCard, Object.assign({}, res, { loading: loading, layout: 'vertical' // TODO:: change based on container size may be
                 }));
            return res;
        }
        return details;
    }, [row, loading, activeTab]);
    const renderTitle = useCallback(() => {
        if (typeof title === 'function') {
            const res = title(row, loading);
            if (isObjectDetailsPanelHeaderProps(res)) {
                return _jsx(ObjectDetailsPanelHeader, Object.assign({}, res, { item: row, loading: loading }));
            }
            return res;
        }
        if (isObjectDetailsPanelHeaderProps(title)) {
            return _jsx(ObjectDetailsPanelHeader, Object.assign({}, title, { item: row, loading: loading }));
        }
        return renderDetails(title);
    }, [row, loading, title, renderDetails]);
    const renderNotFound = useCallback((message) => {
        return _jsx("div", { className: "uxpcore_obsdp__additional_details_notfound", children: message });
    }, []);
    const activeAdditionalDetailsTab = useMemo(() => {
        if (!hasAdditionalDetails)
            return null;
        const tabId = (appendToURL && ad) ? ad : selectedAdditionlDetailsTab;
        if (!tabId)
            return null;
        const found = allAdditionalDetails.find(t => t.id === tabId);
        return found ? found.id : null;
    }, [appendToURL, ad, selectedAdditionlDetailsTab, allAdditionalDetails, hasAdditionalDetails]);
    const isAdditionalDetailsPanelOpen = !!activeAdditionalDetailsTab;
    const renderAdditionalDetails = useCallback(() => {
        if (!row && !loading)
            return renderNotFound(context.$L('uxp-core.text.data-not-available'));
        if (!activeAdditionalDetailsTab && !loading)
            return renderNotFound(context.$L('uxp-core.text.select-a-tab'));
        const additionalDetailsTab = allAdditionalDetails.find((ad) => ad.id === activeAdditionalDetailsTab);
        if (!additionalDetailsTab && !loading)
            return renderNotFound(context.$L('uxp-core.text.selected-tab-not-found'));
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_obsdp__additional_details_header", children: [_jsx("div", { className: "uxpcore_obsdp__additional_details_header-icon", children: loading
                                ? _jsx(SkeletonLoader, { width: "1rem", height: "1rem", additionalStyle: { borderRadius: '1rem' } })
                                : _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.icon) }) }), _jsx("div", { className: "uxpcore_obsdp__additional_details_header-label", children: loading
                                ? _jsx(SkeletonLoader, { width: "5rem", height: "1rem" })
                                : additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.label }), (additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.refreshable) && !loading && (_jsx("div", { className: "uxpcore_obsdp__additional_details_header-refresh", onClick: () => setAdditionalDetailsRefreshKeys(prev => (Object.assign(Object.assign({}, prev), { [activeAdditionalDetailsTab]: (prev[activeAdditionalDetailsTab] || 0) + 1 }))), children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon('fas rotate-right') }) }))] }), _jsx("div", { className: "uxpcore_obsdp__additional_details_body", children: _jsx(ErrorBoundary, { children: renderDetails(additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.content) }) }, additionalDetailsRefreshKeys[activeAdditionalDetailsTab] || 0)] });
    }, [allAdditionalDetails, activeAdditionalDetailsTab, renderDetails, loading, row, additionalDetailsRefreshKeys]);
    const renderToolbarItem = useCallback((item, key, showLabel = true) => {
        if (loading) {
            return _jsx("div", { className: "uxpcore_obsdp__toolbar_item", children: showLabel
                    ? _jsx(SkeletonLoader, { width: "5rem", height: "1.5rem" })
                    : _jsx(SkeletonLoader, { width: "1.5rem", height: "1.5rem", additionalStyle: { borderRadius: '1rem' } }) }, key);
        }
        function renderToolbarButton() {
            return _jsxs("div", { className: "uxpcore_obsdp__toolbar_item", onClick: (e) => {
                    if (!!(item === null || item === void 0 ? void 0 : item.renderOptionsDropdown))
                        return;
                    if (row && (item === null || item === void 0 ? void 0 : item.onClick))
                        item.onClick(e, row);
                }, children: [_jsx("div", { className: "uxpcore_obsdp__toolbar_item-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(item.icon) }) }), showLabel
                        ? _jsx("div", { className: "uxpcore_obsdp__toolbar_item-label", children: context.$L(item.label) })
                        : null] }, key);
        }
        if (!!(item === null || item === void 0 ? void 0 : item.renderOptionsDropdown)) {
            const toolbarDropdownRef = useRef(null);
            const dropdownContent = Array.isArray(item.renderOptionsDropdown)
                ? _jsx(ActionsListComponent, { actions: item.renderOptionsDropdown, item: row, parentDropdownRef: toolbarDropdownRef })
                : item.renderOptionsDropdown;
            return _jsx(Dropdown, { ref: toolbarDropdownRef, trigger: renderToolbarButton(), content: dropdownContent, position: 'bottom-left' }, key);
        }
        return renderToolbarButton();
    }, [row, loading]);
    const OVERFLOW_BTN_WIDTH = 80;
    const measureTabOverflow = useCallback(() => {
        setTabOverflowFrom(null);
        requestAnimationFrame(() => {
            var _a, _b, _c;
            effectiveTabs.forEach(tab => {
                const el = tabItemRefs.current.get(tab.id);
                if (el && el.offsetWidth > 0)
                    tabWidthCache.current.set(tab.id, el.offsetWidth);
            });
            const wrapperWidth = (_b = (_a = tabsContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
            if (wrapperWidth === 0)
                return;
            const total = effectiveTabs.reduce((s, t) => { var _a; return s + ((_a = tabWidthCache.current.get(t.id)) !== null && _a !== void 0 ? _a : 0); }, 0);
            if (total <= wrapperWidth) {
                setTabOverflowFrom(null);
                return;
            }
            let acc = 0;
            for (let i = 0; i < effectiveTabs.length; i++) {
                const w = (_c = tabWidthCache.current.get(effectiveTabs[i].id)) !== null && _c !== void 0 ? _c : 0;
                if (acc + w + OVERFLOW_BTN_WIDTH > wrapperWidth) {
                    setTabOverflowFrom(i);
                    return;
                }
                acc += w;
            }
            setTabOverflowFrom(null);
        });
    }, [effectiveTabs]);
    useEffect(() => { measureTabOverflow(); }, [effectiveTabs]);
    useEffect(() => {
        window.addEventListener('resize', measureTabOverflow);
        return () => window.removeEventListener('resize', measureTabOverflow);
    }, [measureTabOverflow]);
    const measureToolbarLeftOverflow = useCallback(() => {
        const items = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) || []).filter(t => !t.visible || (row && t.visible(row)));
        setToolbarLeftOverflowFrom(null);
        requestAnimationFrame(() => {
            var _a, _b, _c;
            items.forEach((_, i) => {
                const el = toolbarLeftItemRefs.current.get(String(i));
                if (el && el.offsetWidth > 0)
                    toolbarLeftWidthCache.current.set(String(i), el.offsetWidth);
            });
            const wrapperWidth = (_b = (_a = toolbarLeftContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
            if (wrapperWidth === 0)
                return;
            const total = items.reduce((s, _, i) => { var _a; return s + ((_a = toolbarLeftWidthCache.current.get(String(i))) !== null && _a !== void 0 ? _a : 0); }, 0);
            if (total <= wrapperWidth) {
                setToolbarLeftOverflowFrom(null);
                return;
            }
            let acc = 0;
            for (let i = 0; i < items.length; i++) {
                const w = (_c = toolbarLeftWidthCache.current.get(String(i))) !== null && _c !== void 0 ? _c : 0;
                if (acc + w + OVERFLOW_BTN_WIDTH > wrapperWidth) {
                    setToolbarLeftOverflowFrom(i);
                    return;
                }
                acc += w;
            }
            setToolbarLeftOverflowFrom(null);
        });
    }, [resolvedToolbarItems, row]);
    const measureToolbarRightOverflow = useCallback(() => {
        const items = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) || []).filter(t => !t.visible || (row && t.visible(row)));
        setToolbarRightOverflowFrom(null);
        requestAnimationFrame(() => {
            var _a, _b, _c;
            items.forEach((_, i) => {
                const el = toolbarRightItemRefs.current.get(String(i));
                if (el && el.offsetWidth > 0)
                    toolbarRightWidthCache.current.set(String(i), el.offsetWidth);
            });
            const wrapperWidth = (_b = (_a = toolbarRightContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
            if (wrapperWidth === 0)
                return;
            const total = items.reduce((s, _, i) => { var _a; return s + ((_a = toolbarRightWidthCache.current.get(String(i))) !== null && _a !== void 0 ? _a : 0); }, 0);
            if (total <= wrapperWidth) {
                setToolbarRightOverflowFrom(null);
                return;
            }
            let acc = 0;
            for (let i = 0; i < items.length; i++) {
                const w = (_c = toolbarRightWidthCache.current.get(String(i))) !== null && _c !== void 0 ? _c : 0;
                if (acc + w + OVERFLOW_BTN_WIDTH > wrapperWidth) {
                    setToolbarRightOverflowFrom(i);
                    return;
                }
                acc += w;
            }
            setToolbarRightOverflowFrom(null);
        });
    }, [resolvedToolbarItems, row]);
    useEffect(() => { measureToolbarLeftOverflow(); measureToolbarRightOverflow(); }, [resolvedToolbarItems, row]);
    useEffect(() => {
        window.addEventListener('resize', measureToolbarLeftOverflow);
        window.addEventListener('resize', measureToolbarRightOverflow);
        return () => {
            window.removeEventListener('resize', measureToolbarLeftOverflow);
            window.removeEventListener('resize', measureToolbarRightOverflow);
        };
    }, [measureToolbarLeftOverflow, measureToolbarRightOverflow]);
    const renderTabNavigation = useCallback(() => {
        var _a, _b;
        if (!shouldShowTabs)
            return null;
        const handleTabClick = (tabId) => {
            var _a;
            (_a = tabOverflowDropdownRef.current) === null || _a === void 0 ? void 0 : _a.close();
            setTabOverflowOpen(false);
            if (appendToURL) {
                navigate(generateURL({ searchParams: { [urlParams.detailsTab]: tabId } }));
            }
            else {
                setSelectedTabId(tabId);
            }
        };
        const overflowTabs = tabOverflowFrom !== null ? effectiveTabs.slice(tabOverflowFrom) : [];
        const activeOverflowTab = (_a = overflowTabs.find(tab => tab.id === (activeTab === null || activeTab === void 0 ? void 0 : activeTab.id))) !== null && _a !== void 0 ? _a : null;
        const overflowBtnLabel = (_b = activeOverflowTab === null || activeOverflowTab === void 0 ? void 0 : activeOverflowTab.label) !== null && _b !== void 0 ? _b : 'More';
        return (_jsxs("div", { ref: tabsContainerRef, className: "uxpcore_obsdp__tabs_container", children: [effectiveTabs.map((tab, i) => (_jsxs("div", { ref: el => { if (el)
                        tabItemRefs.current.set(tab.id, el); }, className: classNames('uxpcore_obsdp__tab', { 'uxpcore_obsdp__tab--active': (activeTab === null || activeTab === void 0 ? void 0 : activeTab.id) === tab.id }, { 'uxpcore_obsdp__tab--hidden': tabOverflowFrom !== null && i >= tabOverflowFrom }), onClick: () => handleTabClick(tab.id), children: [tab.icon && (_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(tab.icon) }) })), tab.label && (_jsx("div", { className: "uxpcore_obsdp__tab-label", children: tab.label }))] }, tab.id))), overflowTabs.length > 0 && (_jsx(Dropdown, { ref: tabOverflowDropdownRef, trigger: _jsxs("div", { className: classNames('uxpcore_obsdp__tab', { 'uxpcore_obsdp__tab--active': !!activeOverflowTab }), children: [overflowBtnLabel, _jsx(DropdownIndicator, { isOpen: tabOverflowOpen })] }), isOpen: tabOverflowOpen, onToggle: () => setTabOverflowOpen(p => !p), content: _jsx("div", { className: "uxpcore_obsdp__overflow_menu", children: overflowTabs.map(tab => (_jsxs("div", { className: classNames('uxpcore_obsdp__overflow_item', { 'uxpcore_obsdp__overflow_item--active': (activeTab === null || activeTab === void 0 ? void 0 : activeTab.id) === tab.id }), onClick: () => handleTabClick(tab.id), children: [tab.icon && (_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(tab.icon) }) })), tab.label && (_jsx("div", { className: "uxpcore_obsdp__tab-label", children: tab.label }))] }, tab.id))) }), position: "bottom-left" }))] }));
    }, [shouldShowTabs, effectiveTabs, activeTab, appendToURL, navigate, generateURL, urlParams, tabOverflowFrom, tabOverflowOpen]);
    return _jsxs("div", { className: "uxpcore_obsdp__container", children: [_jsxs("div", { className: "uxpcore_obsdp__content_container", children: [_jsx("div", { className: classNames("uxpcore_obsdp__titile_container", {
                            'uxpcore_obsdp__titile_container--title-only': typeof title === 'string'
                        }), children: _jsx(ErrorBoundary, { children: renderTitle() }) }), renderTabNavigation(), hasToolbarItems && (() => {
                        const visibleLeftItems = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) || []).filter(t => !t.visible || (row && t.visible(row)));
                        const visibleRightItems = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) || []).filter(t => !t.visible || (row && t.visible(row)));
                        const leftOverflowItems = toolbarLeftOverflowFrom !== null ? visibleLeftItems.slice(toolbarLeftOverflowFrom) : [];
                        const rightOverflowItems = toolbarRightOverflowFrom !== null ? visibleRightItems.slice(toolbarRightOverflowFrom) : [];
                        return (_jsxs("div", { className: "uxpcore_obsdp__toolbar_container", children: [_jsxs("div", { ref: toolbarLeftContainerRef, className: "uxpcore_obsdp__toolbar_items uxpcore_obsdp__toolbar_items-left", children: [visibleLeftItems.map((t, i) => (_jsx("div", { ref: el => { if (el)
                                                toolbarLeftItemRefs.current.set(String(i), el); }, className: classNames('uxpcore_obsdp__toolbar_item_wrapper', {
                                                'uxpcore_obsdp__toolbar_item_wrapper--hidden': toolbarLeftOverflowFrom !== null && i >= toolbarLeftOverflowFrom
                                            }), children: renderToolbarItem(t, i) }, i))), leftOverflowItems.length > 0 && (_jsx(Dropdown, { ref: toolbarLeftOverflowDropdownRef, trigger: _jsx("div", { className: "uxpcore_obsdp__toolbar_item uxpcore_obsdp__toolbar_overflow_btn", children: _jsx(DropdownIndicator, { isOpen: toolbarLeftOverflowOpen }) }), isOpen: toolbarLeftOverflowOpen, onToggle: () => setToolbarLeftOverflowOpen(p => !p), content: _jsx("div", { className: "uxpcore_obsdp__overflow_menu", children: leftOverflowItems.map((item, i) => (_jsxs("div", { className: "uxpcore_obsdp__overflow_item", onClick: (e) => {
                                                        var _a, _b;
                                                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, e, row);
                                                        (_b = toolbarLeftOverflowDropdownRef.current) === null || _b === void 0 ? void 0 : _b.close();
                                                        setToolbarLeftOverflowOpen(false);
                                                    }, children: [_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(item.icon) }) }), _jsx("div", { className: "uxpcore_obsdp__tab-label", children: context.$L(item.label) })] }, i))) }), position: "bottom-left" }))] }), _jsxs("div", { ref: toolbarRightContainerRef, className: "uxpcore_obsdp__toolbar_items uxpcore_obsdp__toolbar_items-right", children: [visibleRightItems.map((t, i) => (_jsx("div", { ref: el => { if (el)
                                                toolbarRightItemRefs.current.set(String(i), el); }, className: classNames('uxpcore_obsdp__toolbar_item_wrapper', {
                                                'uxpcore_obsdp__toolbar_item_wrapper--hidden': toolbarRightOverflowFrom !== null && i >= toolbarRightOverflowFrom
                                            }), children: renderToolbarItem(t, i, false) }, i))), rightOverflowItems.length > 0 && (_jsx(Dropdown, { ref: toolbarRightOverflowDropdownRef, trigger: _jsx("div", { className: "uxpcore_obsdp__toolbar_item uxpcore_obsdp__toolbar_overflow_btn", children: _jsx(DropdownIndicator, { isOpen: toolbarRightOverflowOpen }) }), isOpen: toolbarRightOverflowOpen, onToggle: () => setToolbarRightOverflowOpen(p => !p), content: _jsx("div", { className: "uxpcore_obsdp__overflow_menu", children: rightOverflowItems.map((item, i) => (_jsxs("div", { className: "uxpcore_obsdp__overflow_item", onClick: (e) => {
                                                        var _a, _b;
                                                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, e, row);
                                                        (_b = toolbarRightOverflowDropdownRef.current) === null || _b === void 0 ? void 0 : _b.close();
                                                        setToolbarRightOverflowOpen(false);
                                                    }, children: [_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(item.icon) }) }), _jsx("div", { className: "uxpcore_obsdp__tab-label", children: context.$L(item.label) })] }, i))) }), position: "bottom-right" }))] })] }));
                    })(), _jsxs("div", { className: "uxpcore_obsdp__details_container", children: [_jsxs("div", { className: classNames("uxpcore_obsdp__main_details_container", {
                                    "uxpcore_obsdp__main_details_container--other-only": !hasGeneralDetails && hasOtherDetails
                                }), children: [hasGeneralDetails && (_jsx("div", { className: "uxpcore_obsdp__general_details_container", children: _jsx(ErrorBoundary, { children: renderGeneralDetails() }) })), hasOtherDetails && (_jsx("div", { className: classNames("uxpcore_obsdp__other_details_container", {
                                            "uxpcore_obsdp__other_details_container--full-height": !hasGeneralDetails
                                        }), children: _jsx(ErrorBoundary, { children: renderDetails(otherDetails) }) }))] }), _jsx("div", { className: classNames("uxpcore_obsdp__additional_details_container", {
                                    "uxpcore_obsdp__additional_details_container--open": isAdditionalDetailsPanelOpen
                                }), children: isAdditionalDetailsPanelOpen && renderAdditionalDetails() })] })] }), (showCloseButton || hasAdditionalDetails)
                ? _jsxs("div", { className: "uxpcore_obsdp__gutter_container", children: [showCloseButton ?
                            _jsx("div", { className: "uxpcore_obsdp__close_btn", onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose === null || onClose === void 0 ? void 0 : onClose();
                                }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'times'] }) })
                            : null, _jsx("div", { className: "uxpcore_obsdp__additional_details_tabs", children: allAdditionalDetails.map((adTab, i) => {
                                const isActive = activeAdditionalDetailsTab === adTab.id && isAdditionalDetailsPanelOpen;
                                return _jsx("div", { className: classNames("uxpcore_obsdp__additional_details_tab", { "uxpcore_obsdp__additional_details_tab--active": isActive }), onClick: (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (appendToURL) {
                                            if (isActive) {
                                                navigate(generateURL({ removeParams: [urlParams.additionalDetailsTab] }));
                                            }
                                            else {
                                                navigate(generateURL({ searchParams: { [urlParams.additionalDetailsTab]: adTab.id } }));
                                            }
                                        }
                                        else {
                                            setSelectedAdditionalDetailsTab(prev => prev === adTab.id ? null : adTab.id);
                                        }
                                    }, children: _jsx("div", { className: "uxpcore_obsdp__additional_details_tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(adTab.icon) }) }) }, i);
                            }) })] })
                : null] });
});
/**
 * @export
 * A component that displays a details panel for a selected row, with a title, toolbar, and main/additional details sections.
 *
 * @example
 * ```
 * <ObjectDetailsPanel
 *   title="User Details"
 *   data={{ id: '1', name: 'John Doe' }}
 *   generalDetails={<div>General info</div>}
 *   idField="id"
 * />
 * ```
 *
 * @example
 * ```
 * <ObjectDetailsPanel
 *   title={(row) => `Details for ${row.name}`}
 *   data={async () => ({ id: '1', name: 'John Doe', email: 'john@example.com' })}
 *   generalDetails={(row) => ({ fields: [{ label: 'Name', value: row.name }, { label: 'Email', value: row.email }] })}
 *   toolbarItems={{
 *     left: [{ icon: 'edit', label: 'Edit', onClick: (e, row) => console.log('Edit', row) }],
 *     right: [{ icon: 'share', label: 'Share' }]
 *   }}
 *   otherDetails={<div>Additional info</div>}
 *   additionlDetails={[
 *     { id: 'notes', icon: 'note', label: 'Notes', content: 'Sample notes' },
 *     { id: 'history', icon: 'history', label: 'History', content: (row) => <div>History for {row.name}</div> }
 *   ]}
 *   showCloseButton={true}
 *   onClose={() => console.log('Panel closed')}
 * />
 * ```
 */
export const ObjectDetailsPanel = memo(ObjectDetailsPanelBase);
